/**
 * Escalação de recovery: soft → hard → critical → safe_mode.
 */

import {
  enterStorageSafeMode,
  clearStorageSafeMode,
} from '../storage/storageSafeMode';
import { setRuntimeModeExplicit } from '../runtime/runtimeMode';
import { recordRecoveryEscalation } from '../telemetry';

export type RecoveryLevel = 'soft' | 'hard' | 'critical' | 'safe_mode';

export interface RecoveryEscalationSignals {
  indexMismatch?: boolean;
  partitionOverflow?: boolean;
  replayInterrupted?: boolean;
  replayCursorInvalid?: boolean;
  generationCorrupt?: boolean;
  shadowCommitFailed?: boolean;
  replayOwnershipMismatch?: boolean;
  validateFailed?: boolean;
}

let currentLevel: RecoveryLevel = 'soft';
let validateFailStreak = 0;

export function escalateRecovery(signals: RecoveryEscalationSignals): RecoveryLevel {
  let level: RecoveryLevel = 'soft';

  if (
    signals.generationCorrupt ||
    signals.shadowCommitFailed ||
    signals.replayOwnershipMismatch
  ) {
    level = 'critical';
  } else if (signals.replayInterrupted || signals.replayCursorInvalid) {
    level = 'hard';
  } else if (signals.indexMismatch || signals.partitionOverflow) {
    level = 'soft';
  }

  if (signals.validateFailed) {
    validateFailStreak++;
    if (validateFailStreak >= 2) level = 'safe_mode';
    else level = level === 'soft' ? 'hard' : 'critical';
  } else {
    validateFailStreak = 0;
  }

  const signalCount = [
    signals.indexMismatch,
    signals.partitionOverflow,
    signals.replayInterrupted,
    signals.replayCursorInvalid,
    signals.generationCorrupt,
    signals.shadowCommitFailed,
    signals.validateFailed,
  ].filter(Boolean).length;
  if (signalCount >= 3) level = 'safe_mode';

  if (level === 'safe_mode') {
    enterStorageSafeMode('recovery_escalation');
    setRuntimeModeExplicit('safe_mode');
  }

  currentLevel = level;
  recordRecoveryEscalation(level);
  return level;
}

export function getRecoveryLevel(): RecoveryLevel {
  return currentLevel;
}

export function _testResetRecoveryEscalation(): void {
  currentLevel = 'soft';
  validateFailStreak = 0;
  clearStorageSafeMode();
}
